/**
 * Configurações visuais dos templates de LovePage.
 * Cada template define paleta, fundo, tipografia e elementos decorativos.
 * Usado por: ThemeCard, GiftPreview, PublicGiftPage
 */

export interface TemplateConfig {
  slug: string;
  name: string;

  // ─── Thumbnail do card de seleção ─────────────────────────────────────────
  // CSS inline para o preview no ThemeCard (sem imagem externa)
  thumbnailGradient: string;
  thumbnailAccent: string;    // cor do coração/símbolo no thumb
  thumbnailLabel: string;     // texto de exemplo no thumb
  thumbnailDark: boolean;     // texto claro ou escuro sobre o gradiente

  // ─── Página pública e preview ──────────────────────────────────────────────
  pageBackground: string;     // fundo geral da página (inline style ou classe)
  pageBgClass: string;        // classe Tailwind para o fundo geral

  headerGradient: string;     // gradient do hero header
  headerTextClass: string;    // cor do texto do header
  headerAccentClass: string;  // cor do subtítulo/nomes

  bodyBgClass: string;        // fundo dos cards/seções
  bodyTextClass: string;      // texto principal
  accentClass: string;        // ícones, bordas, labels
  accentSecondary: string;    // cor hex do acento secundário
  accentHex: string;          // cor hex principal para inline styles

  sectionBorderClass: string; // borda dos cards de seção
  sectionHeaderBg: string;    // fundo do header de cada seção (ex: "Carta de amor")

  counterBgClass: string;     // fundo dos boxes do contador
  counterTextClass: string;   // número do contador
  counterLabelClass: string;  // label do contador ("anos", "dias"...)

  letterBgClass: string;      // fundo da seção da carta
  letterTextClass: string;    // texto da carta
  letterItalic: boolean;

  musicBgClass: string;       // fundo do player de música
  musicIconBg: string;        // fundo do ícone do Spotify

  footerTextClass: string;
  footerBrandClass: string;

  // ─── Decoração ────────────────────────────────────────────────────────────
  symbol: string;             // símbolo decorativo (♥ ✦ ★ ◆)
  symbolClass: string;        // cor do símbolo
  fontClass: string;          // "font-serif" | "font-sans" | ""

  // Partículas/decoração flutuante
  particleColor: string;      // cor das partículas flutuantes
  particleSymbol: string;
}

export const TEMPLATE_CONFIGS: Record<string, TemplateConfig> = {
  // ─── 1. Romance Clássico (BASIC) ───────────────────────────────────────────
  "classic-romance": {
    slug: "classic-romance",
    name: "Romance Clássico",
    thumbnailGradient: "linear-gradient(135deg, #FFE4E6 0%, #FECDD3 40%, #FDA4AF 100%)",
    thumbnailAccent: "#E11D48",
    thumbnailLabel: "Clássico & Elegante",
    thumbnailDark: false,
    pageBackground: "#FFF8FA",
    pageBgClass: "bg-[#FFF8FA]",
    headerGradient: "linear-gradient(135deg, #E11D48 0%, #881337 100%)",
    headerTextClass: "text-white",
    headerAccentClass: "text-rose-200",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-[#1F2937]",
    accentClass: "text-[#E11D48]",
    accentSecondary: "#881337",
    accentHex: "#E11D48",
    sectionBorderClass: "border-rose-100",
    sectionHeaderBg: "bg-gradient-to-r from-rose-50 to-orange-50",
    counterBgClass: "bg-white border-rose-100",
    counterTextClass: "text-[#E11D48]",
    counterLabelClass: "text-gray-400",
    letterBgClass: "bg-white",
    letterTextClass: "text-gray-700",
    letterItalic: true,
    musicBgClass: "bg-rose-50",
    musicIconBg: "bg-[#E11D48]",
    footerTextClass: "text-gray-300",
    footerBrandClass: "text-[#E11D48]",
    symbol: "♥",
    symbolClass: "text-rose-200",
    fontClass: "",
    particleColor: "#FECDD3",
    particleSymbol: "♥",
  },

  // ─── 2. Jardim de Rosas (BASIC) ────────────────────────────────────────────
  "rose-garden": {
    slug: "rose-garden",
    name: "Jardim de Rosas",
    thumbnailGradient: "linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 50%, #F9A8D4 100%)",
    thumbnailAccent: "#DB2777",
    thumbnailLabel: "Floral & Delicado",
    thumbnailDark: false,
    pageBackground: "#FDF2F8",
    pageBgClass: "bg-pink-50",
    headerGradient: "linear-gradient(135deg, #DB2777 0%, #9D174D 100%)",
    headerTextClass: "text-white",
    headerAccentClass: "text-pink-200",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-[#1F2937]",
    accentClass: "text-pink-600",
    accentSecondary: "#9D174D",
    accentHex: "#DB2777",
    sectionBorderClass: "border-pink-100",
    sectionHeaderBg: "bg-gradient-to-r from-pink-50 to-fuchsia-50",
    counterBgClass: "bg-white border-pink-100",
    counterTextClass: "text-pink-600",
    counterLabelClass: "text-pink-300",
    letterBgClass: "bg-pink-50",
    letterTextClass: "text-pink-900",
    letterItalic: true,
    musicBgClass: "bg-pink-50",
    musicIconBg: "bg-pink-600",
    footerTextClass: "text-pink-200",
    footerBrandClass: "text-pink-400",
    symbol: "❀",
    symbolClass: "text-pink-200",
    fontClass: "",
    particleColor: "#F9A8D4",
    particleSymbol: "❀",
  },

  // ─── 3. Heartfelt (BASIC) ──────────────────────────────────────────────────
  "heartfelt": {
    slug: "heartfelt",
    name: "Heartfelt",
    thumbnailGradient: "linear-gradient(160deg, #FFFFFF 0%, #FFF0F3 60%, #FFE4E6 100%)",
    thumbnailAccent: "#E11D48",
    thumbnailLabel: "Puro & Minimalista",
    thumbnailDark: false,
    pageBackground: "#FFFFFF",
    pageBgClass: "bg-white",
    headerGradient: "linear-gradient(180deg, #FFF0F3 0%, #FFE4E6 100%)",
    headerTextClass: "text-[#881337]",
    headerAccentClass: "text-rose-400",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-[#1F2937]",
    accentClass: "text-[#E11D48]",
    accentSecondary: "#E11D48",
    accentHex: "#E11D48",
    sectionBorderClass: "border-rose-50",
    sectionHeaderBg: "bg-rose-50",
    counterBgClass: "bg-rose-50 border-rose-100",
    counterTextClass: "text-[#881337]",
    counterLabelClass: "text-rose-300",
    letterBgClass: "bg-white",
    letterTextClass: "text-gray-600",
    letterItalic: false,
    musicBgClass: "bg-rose-50",
    musicIconBg: "bg-[#E11D48]",
    footerTextClass: "text-rose-200",
    footerBrandClass: "text-[#E11D48]",
    symbol: "♡",
    symbolClass: "text-rose-200",
    fontClass: "",
    particleColor: "#FFE4E6",
    particleSymbol: "♡",
  },

  // ─── 4. Eternidade (PREMIUM) ───────────────────────────────────────────────
  "eternidade": {
    slug: "eternidade",
    name: "Eternidade",
    thumbnailGradient: "linear-gradient(135deg, #FFF1F2 0%, #FFE4E6 30%, #FECDD3 60%, #FDE68A 100%)",
    thumbnailAccent: "#B45309",
    thumbnailLabel: "Rosa & Dourado",
    thumbnailDark: false,
    pageBackground: "#FFF5F7",
    pageBgClass: "bg-[#FFF5F7]",
    headerGradient: "linear-gradient(135deg, #FB7185 0%, #E11D48 50%, #881337 100%)",
    headerTextClass: "text-white",
    headerAccentClass: "text-amber-200",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-[#7C2D12]",
    accentClass: "text-amber-600",
    accentSecondary: "#B45309",
    accentHex: "#F59E0B",
    sectionBorderClass: "border-amber-100",
    sectionHeaderBg: "bg-gradient-to-r from-amber-50 to-rose-50",
    counterBgClass: "bg-white border-amber-200",
    counterTextClass: "text-amber-600",
    counterLabelClass: "text-amber-400",
    letterBgClass: "bg-amber-50",
    letterTextClass: "text-rose-900",
    letterItalic: true,
    musicBgClass: "bg-amber-50",
    musicIconBg: "bg-amber-500",
    footerTextClass: "text-amber-200",
    footerBrandClass: "text-amber-400",
    symbol: "✦",
    symbolClass: "text-amber-300",
    fontClass: "font-serif",
    particleColor: "#FDE68A",
    particleSymbol: "✦",
  },

  // ─── 5. Céu Estrelado (PREMIUM) ────────────────────────────────────────────
  "ceu-estrelado": {
    slug: "ceu-estrelado",
    name: "Céu Estrelado",
    thumbnailGradient: "linear-gradient(160deg, #0F0C29 0%, #1A1040 40%, #24243e 70%, #0F2027 100%)",
    thumbnailAccent: "#F59E0B",
    thumbnailLabel: "Noturno & Mágico",
    thumbnailDark: true,
    pageBackground: "#0D0B21",
    pageBgClass: "bg-[#0D0B21]",
    headerGradient: "linear-gradient(180deg, #1A1040 0%, #0D0B21 100%)",
    headerTextClass: "text-amber-300",
    headerAccentClass: "text-amber-400",
    bodyBgClass: "bg-[#141230]",
    bodyTextClass: "text-amber-100",
    accentClass: "text-amber-400",
    accentSecondary: "#D97706",
    accentHex: "#F59E0B",
    sectionBorderClass: "border-amber-900/40",
    sectionHeaderBg: "bg-[#1A1040]",
    counterBgClass: "bg-[#1A1040] border-amber-800/50",
    counterTextClass: "text-amber-400",
    counterLabelClass: "text-amber-700",
    letterBgClass: "bg-[#1A1040]",
    letterTextClass: "text-amber-100",
    letterItalic: true,
    musicBgClass: "bg-[#1A1040]",
    musicIconBg: "bg-amber-500",
    footerTextClass: "text-amber-800",
    footerBrandClass: "text-amber-400",
    symbol: "★",
    symbolClass: "text-amber-400/30",
    fontClass: "font-serif",
    particleColor: "#F59E0B",
    particleSymbol: "★",
  },

  // ─── 6. Memórias Polaroid (PREMIUM) ────────────────────────────────────────
  "memorias-polaroid": {
    slug: "memorias-polaroid",
    name: "Memórias Polaroid",
    thumbnailGradient: "linear-gradient(135deg, #FDF8F0 0%, #F5EDDC 50%, #EAD9C0 100%)",
    thumbnailAccent: "#92400E",
    thumbnailLabel: "Vintage & Nostálgico",
    thumbnailDark: false,
    pageBackground: "#FAF4E8",
    pageBgClass: "bg-[#FAF4E8]",
    headerGradient: "linear-gradient(135deg, #92400E 0%, #78350F 100%)",
    headerTextClass: "text-amber-50",
    headerAccentClass: "text-amber-200",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-amber-900",
    accentClass: "text-amber-700",
    accentSecondary: "#78350F",
    accentHex: "#92400E",
    sectionBorderClass: "border-amber-200",
    sectionHeaderBg: "bg-amber-50",
    counterBgClass: "bg-amber-50 border-amber-200",
    counterTextClass: "text-amber-700",
    counterLabelClass: "text-amber-400",
    letterBgClass: "bg-white",
    letterTextClass: "text-amber-800",
    letterItalic: true,
    musicBgClass: "bg-amber-50",
    musicIconBg: "bg-amber-700",
    footerTextClass: "text-amber-300",
    footerBrandClass: "text-amber-600",
    symbol: "◈",
    symbolClass: "text-amber-200",
    fontClass: "",
    particleColor: "#EAD9C0",
    particleSymbol: "◈",
  },

  // ─── 7. L'Amour (PREMIUM) ──────────────────────────────────────────────────
  "lamour": {
    slug: "lamour",
    name: "L'Amour",
    thumbnailGradient: "linear-gradient(160deg, #FAFAF8 0%, #F5F0E8 50%, #EDE8DC 100%)",
    thumbnailAccent: "#7C2D12",
    thumbnailLabel: "Carta & Envelope",
    thumbnailDark: false,
    pageBackground: "#FAFAF8",
    pageBgClass: "bg-[#FAFAF8]",
    headerGradient: "linear-gradient(160deg, #F5F0E8 0%, #EDE8DC 100%)",
    headerTextClass: "text-[#7C2D12]",
    headerAccentClass: "text-rose-700",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-[#3D1A0A]",
    accentClass: "text-[#9F1239]",
    accentSecondary: "#7C2D12",
    accentHex: "#9F1239",
    sectionBorderClass: "border-stone-200",
    sectionHeaderBg: "bg-stone-50",
    counterBgClass: "bg-stone-50 border-stone-200",
    counterTextClass: "text-[#9F1239]",
    counterLabelClass: "text-stone-400",
    letterBgClass: "bg-white",
    letterTextClass: "text-stone-700",
    letterItalic: true,
    musicBgClass: "bg-stone-50",
    musicIconBg: "bg-[#9F1239]",
    footerTextClass: "text-stone-300",
    footerBrandClass: "text-[#9F1239]",
    symbol: "✉",
    symbolClass: "text-stone-200",
    fontClass: "font-serif",
    particleColor: "#EDE8DC",
    particleSymbol: "✉",
  },

  // ─── 8. CineAmour (DELUXE) ─────────────────────────────────────────────────
  "cine-amour": {
    slug: "cine-amour",
    name: "CineAmour",
    thumbnailGradient: "linear-gradient(160deg, #0A0A0A 0%, #1C1C1C 50%, #0D0D0D 100%)",
    thumbnailAccent: "#F59E0B",
    thumbnailLabel: "Cinematográfico",
    thumbnailDark: true,
    pageBackground: "#0A0A0A",
    pageBgClass: "bg-[#0A0A0A]",
    headerGradient: "linear-gradient(180deg, #1C1C1C 0%, #0A0A0A 100%)",
    headerTextClass: "text-amber-300",
    headerAccentClass: "text-amber-500",
    bodyBgClass: "bg-[#111111]",
    bodyTextClass: "text-stone-200",
    accentClass: "text-amber-400",
    accentSecondary: "#D97706",
    accentHex: "#F59E0B",
    sectionBorderClass: "border-stone-800",
    sectionHeaderBg: "bg-[#1C1C1C]",
    counterBgClass: "bg-[#1C1C1C] border-stone-700",
    counterTextClass: "text-amber-400",
    counterLabelClass: "text-stone-500",
    letterBgClass: "bg-[#111111]",
    letterTextClass: "text-stone-300",
    letterItalic: false,
    musicBgClass: "bg-[#1C1C1C]",
    musicIconBg: "bg-amber-500",
    footerTextClass: "text-stone-700",
    footerBrandClass: "text-amber-500",
    symbol: "◆",
    symbolClass: "text-amber-900/50",
    fontClass: "font-serif",
    particleColor: "#F59E0B",
    particleSymbol: "◆",
  },

  // ─── 9. Carta de Amor (real template) ──────────────────────────────────────
  "carta-amor": {
    slug: "carta-amor",
    name: "Carta de Amor",
    thumbnailGradient: "linear-gradient(160deg, #FAFAF8 0%, #F5F0E8 50%, #EDE8DC 100%)",
    thumbnailAccent: "#7C2D12",
    thumbnailLabel: "Carta & Envelope",
    thumbnailDark: false,
    pageBackground: "#FAFAF8",
    pageBgClass: "bg-[#FAFAF8]",
    headerGradient: "linear-gradient(160deg, #F5F0E8 0%, #EDE8DC 100%)",
    headerTextClass: "text-[#7C2D12]",
    headerAccentClass: "text-rose-700",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-[#3D1A0A]",
    accentClass: "text-[#9F1239]",
    accentSecondary: "#7C2D12",
    accentHex: "#9F1239",
    sectionBorderClass: "border-stone-200",
    sectionHeaderBg: "bg-stone-50",
    counterBgClass: "bg-stone-50 border-stone-200",
    counterTextClass: "text-[#9F1239]",
    counterLabelClass: "text-stone-400",
    letterBgClass: "bg-white",
    letterTextClass: "text-stone-700",
    letterItalic: true,
    musicBgClass: "bg-stone-50",
    musicIconBg: "bg-[#9F1239]",
    footerTextClass: "text-stone-300",
    footerBrandClass: "text-[#9F1239]",
    symbol: "✉",
    symbolClass: "text-stone-200",
    fontClass: "font-serif",
    particleColor: "#EDE8DC",
    particleSymbol: "✉",
  },

  // ─── 10. Filme Romântico (real template) ───────────────────────────────────
  "filme-romantico": {
    slug: "filme-romantico",
    name: "Filme Romântico",
    thumbnailGradient: "linear-gradient(160deg, #0A0A0A 0%, #1C1C1C 50%, #0D0D0D 100%)",
    thumbnailAccent: "#E8C46C",
    thumbnailLabel: "Cinematográfico",
    thumbnailDark: true,
    pageBackground: "#0A0A0A",
    pageBgClass: "bg-[#0A0A0A]",
    headerGradient: "linear-gradient(180deg, #1C1C1C 0%, #0A0A0A 100%)",
    headerTextClass: "text-amber-300",
    headerAccentClass: "text-amber-500",
    bodyBgClass: "bg-[#111111]",
    bodyTextClass: "text-stone-200",
    accentClass: "text-amber-400",
    accentSecondary: "#D97706",
    accentHex: "#E8C46C",
    sectionBorderClass: "border-stone-800",
    sectionHeaderBg: "bg-[#1C1C1C]",
    counterBgClass: "bg-[#1C1C1C] border-stone-700",
    counterTextClass: "text-amber-400",
    counterLabelClass: "text-stone-500",
    letterBgClass: "bg-[#111111]",
    letterTextClass: "text-stone-300",
    letterItalic: false,
    musicBgClass: "bg-[#1C1C1C]",
    musicIconBg: "bg-amber-500",
    footerTextClass: "text-stone-700",
    footerBrandClass: "text-amber-500",
    symbol: "◆",
    symbolClass: "text-amber-900/50",
    fontClass: "font-serif",
    particleColor: "#E8C46C",
    particleSymbol: "◆",
  },

  // ─── 11. Fofo Cute Love (real template) ────────────────────────────────────
  "fofo-cute-love": {
    slug: "fofo-cute-love",
    name: "Fofo Cute Love",
    thumbnailGradient: "linear-gradient(135deg, #FFE4F0 0%, #FFCCE5 50%, #FFB3D9 100%)",
    thumbnailAccent: "#FF6FA8",
    thumbnailLabel: "Fofo & Divertido",
    thumbnailDark: false,
    pageBackground: "#FFF5FA",
    pageBgClass: "bg-[#FFF5FA]",
    headerGradient: "linear-gradient(135deg, #FF8FAB 0%, #FF6FA8 100%)",
    headerTextClass: "text-white",
    headerAccentClass: "text-pink-100",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-pink-900",
    accentClass: "text-pink-500",
    accentSecondary: "#FF6FA8",
    accentHex: "#FF6FA8",
    sectionBorderClass: "border-pink-100",
    sectionHeaderBg: "bg-pink-50",
    counterBgClass: "bg-pink-50 border-pink-100",
    counterTextClass: "text-pink-500",
    counterLabelClass: "text-pink-300",
    letterBgClass: "bg-pink-50",
    letterTextClass: "text-pink-900",
    letterItalic: false,
    musicBgClass: "bg-pink-50",
    musicIconBg: "bg-pink-400",
    footerTextClass: "text-pink-300",
    footerBrandClass: "text-pink-500",
    symbol: "♡",
    symbolClass: "text-pink-300",
    fontClass: "",
    particleColor: "#FFB3D9",
    particleSymbol: "♡",
  },

  // ─── 12. Luxo Preto & Dourado (real template) ──────────────────────────────
  "luxo-preto-dourado": {
    slug: "luxo-preto-dourado",
    name: "Luxo Preto & Dourado",
    thumbnailGradient: "linear-gradient(160deg, #000000 0%, #1A1A1A 50%, #000000 100%)",
    thumbnailAccent: "#D4AF37",
    thumbnailLabel: "Sofisticado & Luxo",
    thumbnailDark: true,
    pageBackground: "#000000",
    pageBgClass: "bg-black",
    headerGradient: "linear-gradient(180deg, #1A1A1A 0%, #000000 100%)",
    headerTextClass: "text-yellow-500",
    headerAccentClass: "text-yellow-600",
    bodyBgClass: "bg-[#0A0A0A]",
    bodyTextClass: "text-neutral-200",
    accentClass: "text-yellow-500",
    accentSecondary: "#B8860B",
    accentHex: "#D4AF37",
    sectionBorderClass: "border-yellow-900/40",
    sectionHeaderBg: "bg-[#1A1A1A]",
    counterBgClass: "bg-[#1A1A1A] border-yellow-900/40",
    counterTextClass: "text-yellow-500",
    counterLabelClass: "text-yellow-700",
    letterBgClass: "bg-[#0A0A0A]",
    letterTextClass: "text-neutral-200",
    letterItalic: true,
    musicBgClass: "bg-[#1A1A1A]",
    musicIconBg: "bg-yellow-600",
    footerTextClass: "text-yellow-900",
    footerBrandClass: "text-yellow-500",
    symbol: "◆",
    symbolClass: "text-yellow-700/40",
    fontClass: "font-serif",
    particleColor: "#D4AF37",
    particleSymbol: "◆",
  },

  // ─── 13. Minimalista Elegante (real template) ──────────────────────────────
  "minimalista-elegante": {
    slug: "minimalista-elegante",
    name: "Minimalista Elegante",
    thumbnailGradient: "linear-gradient(160deg, #FAFAF7 0%, #F0EBE4 50%, #E5DED1 100%)",
    thumbnailAccent: "#92755A",
    thumbnailLabel: "Clean & Elegante",
    thumbnailDark: false,
    pageBackground: "#FAFAF7",
    pageBgClass: "bg-[#FAFAF7]",
    headerGradient: "linear-gradient(180deg, #FAFAF7 0%, #F0EBE4 100%)",
    headerTextClass: "text-stone-800",
    headerAccentClass: "text-stone-500",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-stone-700",
    accentClass: "text-amber-700",
    accentSecondary: "#92755A",
    accentHex: "#92755A",
    sectionBorderClass: "border-stone-200",
    sectionHeaderBg: "bg-stone-50",
    counterBgClass: "bg-stone-50 border-stone-200",
    counterTextClass: "text-stone-800",
    counterLabelClass: "text-stone-400",
    letterBgClass: "bg-white",
    letterTextClass: "text-stone-600",
    letterItalic: false,
    musicBgClass: "bg-stone-50",
    musicIconBg: "bg-stone-700",
    footerTextClass: "text-stone-300",
    footerBrandClass: "text-stone-700",
    symbol: "◦",
    symbolClass: "text-stone-300",
    fontClass: "font-serif",
    particleColor: "#E5DED1",
    particleSymbol: "◦",
  },

  // ─── 14. Polaroid (real template) ──────────────────────────────────────────
  "polaroid": {
    slug: "polaroid",
    name: "Polaroid",
    thumbnailGradient: "linear-gradient(135deg, #FDF8F0 0%, #F5EDDC 50%, #EAD9C0 100%)",
    thumbnailAccent: "#92400E",
    thumbnailLabel: "Vintage & Nostálgico",
    thumbnailDark: false,
    pageBackground: "#FAF4E8",
    pageBgClass: "bg-[#FAF4E8]",
    headerGradient: "linear-gradient(135deg, #92400E 0%, #78350F 100%)",
    headerTextClass: "text-amber-50",
    headerAccentClass: "text-amber-200",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-amber-900",
    accentClass: "text-amber-700",
    accentSecondary: "#78350F",
    accentHex: "#92400E",
    sectionBorderClass: "border-amber-200",
    sectionHeaderBg: "bg-amber-50",
    counterBgClass: "bg-amber-50 border-amber-200",
    counterTextClass: "text-amber-700",
    counterLabelClass: "text-amber-400",
    letterBgClass: "bg-white",
    letterTextClass: "text-amber-800",
    letterItalic: true,
    musicBgClass: "bg-amber-50",
    musicIconBg: "bg-amber-700",
    footerTextClass: "text-amber-300",
    footerBrandClass: "text-amber-600",
    symbol: "◈",
    symbolClass: "text-amber-200",
    fontClass: "",
    particleColor: "#EAD9C0",
    particleSymbol: "◈",
  },

  // ─── 15. Surpresa Interativa (real template) ───────────────────────────────
  "surpresa-interativa": {
    slug: "surpresa-interativa",
    name: "Surpresa Interativa",
    thumbnailGradient: "linear-gradient(135deg, #FFF7E6 0%, #FFE8C2 50%, #FFD49A 100%)",
    thumbnailAccent: "#C2410C",
    thumbnailLabel: "Interativo & Surpresa",
    thumbnailDark: false,
    pageBackground: "#FFF8EC",
    pageBgClass: "bg-[#FFF8EC]",
    headerGradient: "linear-gradient(135deg, #FB923C 0%, #C2410C 100%)",
    headerTextClass: "text-white",
    headerAccentClass: "text-orange-100",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-orange-900",
    accentClass: "text-orange-600",
    accentSecondary: "#C2410C",
    accentHex: "#C2410C",
    sectionBorderClass: "border-orange-100",
    sectionHeaderBg: "bg-orange-50",
    counterBgClass: "bg-orange-50 border-orange-100",
    counterTextClass: "text-orange-700",
    counterLabelClass: "text-orange-300",
    letterBgClass: "bg-orange-50",
    letterTextClass: "text-orange-900",
    letterItalic: false,
    musicBgClass: "bg-orange-50",
    musicIconBg: "bg-orange-600",
    footerTextClass: "text-orange-300",
    footerBrandClass: "text-orange-600",
    symbol: "✦",
    symbolClass: "text-orange-200",
    fontClass: "",
    particleColor: "#FFD49A",
    particleSymbol: "✦",
  },

  // ─── 16. Surpresa Interativa Clean (real template) ─────────────────────────
  "surpresa-interativa-clean": {
    slug: "surpresa-interativa-clean",
    name: "Surpresa Interativa Clean",
    thumbnailGradient: "linear-gradient(160deg, #FAFAFA 0%, #F1F1F1 60%, #E4E4E4 100%)",
    thumbnailAccent: "#475569",
    thumbnailLabel: "Interativo & Clean",
    thumbnailDark: false,
    pageBackground: "#FAFAFA",
    pageBgClass: "bg-[#FAFAFA]",
    headerGradient: "linear-gradient(180deg, #FAFAFA 0%, #F1F1F1 100%)",
    headerTextClass: "text-slate-800",
    headerAccentClass: "text-slate-500",
    bodyBgClass: "bg-white",
    bodyTextClass: "text-slate-700",
    accentClass: "text-slate-600",
    accentSecondary: "#334155",
    accentHex: "#475569",
    sectionBorderClass: "border-slate-200",
    sectionHeaderBg: "bg-slate-50",
    counterBgClass: "bg-slate-50 border-slate-200",
    counterTextClass: "text-slate-700",
    counterLabelClass: "text-slate-400",
    letterBgClass: "bg-white",
    letterTextClass: "text-slate-600",
    letterItalic: false,
    musicBgClass: "bg-slate-50",
    musicIconBg: "bg-slate-700",
    footerTextClass: "text-slate-300",
    footerBrandClass: "text-slate-700",
    symbol: "✧",
    symbolClass: "text-slate-300",
    fontClass: "",
    particleColor: "#E4E4E4",
    particleSymbol: "✧",
  },
};

export const DEFAULT_TEMPLATE = TEMPLATE_CONFIGS["classic-romance"];

export function getTemplateConfig(slug?: string | null): TemplateConfig {
  if (!slug) return DEFAULT_TEMPLATE;
  return TEMPLATE_CONFIGS[slug] ?? DEFAULT_TEMPLATE;
}

/**
 * Fonte única de verdade para os slugs de template.
 * Use este objeto ao referenciar slugs no código — não use strings literais.
 * Quando o backend cadastrar templates, esses slugs devem coincidir exatamente.
 */
export const TEMPLATE_SLUGS = {
  CLASSIC_ROMANCE: "classic-romance",
  ROSE_GARDEN: "rose-garden",
  HEARTFELT: "heartfelt",
  ETERNIDADE: "eternidade",
  CEU_ESTRELADO: "ceu-estrelado",
  MEMORIAS_POLAROID: "memorias-polaroid",
  LAMOUR: "lamour",
  CINE_AMOUR: "cine-amour",
} as const;

export type TemplateSlug = (typeof TEMPLATE_SLUGS)[keyof typeof TEMPLATE_SLUGS];

/** Array de todos os slugs — útil para validação e sincronização com o backend */
export const ALL_TEMPLATE_SLUGS = Object.values(TEMPLATE_SLUGS) as TemplateSlug[];
